import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CurrencyInput } from '@/components/ui/currency-input';
import { SubmitDealStepProps } from '@/types/submit-deal.types';
import { useLanguage } from '@/contexts/LanguageContext';

export const StepProjections = ({ formData, updateFormData, errors, clearError, t }: SubmitDealStepProps) => {
  const { language } = useLanguage();
  const isFr = language === 'fr';

  const handleChange = (field: string, value: string | number | boolean) => {
    updateFormData('projections', { [field]: value });
    clearError(field);
  };

  const sfCollateralOpts = t.step8.sfCollateralOptions as Record<string, string>;

  return (
    <div className="space-y-6">
      {/* AJOUTÉ : Revenu et EBITDA projetés par année — confirmés absents,
          alors qu'attendus par ai.service.ts pour la génération du CIM
          (le formulaire ne collectait auparavant que des pourcentages
          de croissance globaux, sans montants concrets par année). */}
      <div>
        <h4 className="text-lg font-medium mb-3">
          {isFr ? 'Revenus et EBITDA projetés (3 prochaines années)' : 'Projected Revenue & EBITDA (Next 3 Years)'}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="revenueYear1">{isFr ? 'Revenu projeté — An 1' : 'Projected Revenue — Year 1'}</Label>
            <CurrencyInput
              id="revenueYear1"
              min={0}
              value={formData.projections.revenueYear1 || ''}
              onChange={(v) => handleChange('revenueYear1', v === '' ? 0 : parseFloat(v))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="revenueYear2">{isFr ? 'Revenu projeté — An 2' : 'Projected Revenue — Year 2'}</Label>
            <CurrencyInput
              id="revenueYear2"
              min={0}
              value={formData.projections.revenueYear2 || ''}
              onChange={(v) => handleChange('revenueYear2', v === '' ? 0 : parseFloat(v))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="revenueYear3">{isFr ? 'Revenu projeté — An 3' : 'Projected Revenue — Year 3'}</Label>
            <CurrencyInput
              id="revenueYear3"
              min={0}
              value={formData.projections.revenueYear3 || ''}
              onChange={(v) => handleChange('revenueYear3', v === '' ? 0 : parseFloat(v))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ebitdaYear1">{isFr ? 'EBITDA projeté — An 1' : 'Projected EBITDA — Year 1'}</Label>
            <CurrencyInput
              id="ebitdaYear1"
              min={0}
              value={formData.projections.ebitdaYear1 || ''}
              onChange={(v) => handleChange('ebitdaYear1', v === '' ? 0 : parseFloat(v))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ebitdaYear2">{isFr ? 'EBITDA projeté — An 2' : 'Projected EBITDA — Year 2'}</Label>
            <CurrencyInput
              id="ebitdaYear2"
              min={0}
              value={formData.projections.ebitdaYear2 || ''}
              onChange={(v) => handleChange('ebitdaYear2', v === '' ? 0 : parseFloat(v))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ebitdaYear3">{isFr ? 'EBITDA projeté — An 3' : 'Projected EBITDA — Year 3'}</Label>
            <CurrencyInput
              id="ebitdaYear3"
              min={0}
              value={formData.projections.ebitdaYear3 || ''}
              onChange={(v) => handleChange('ebitdaYear3', v === '' ? 0 : parseFloat(v))}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {isFr
            ? 'Facultatif si vous préférez utiliser uniquement les pourcentages de croissance ci-dessous.'
            : 'Optional if you prefer to rely only on the growth percentages below.'}
        </p>
      </div>

      <div className="border-t pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="revenueGrowthForecast">{t.step8.revenueGrowthForecast} *</Label>
          <Input
            id="revenueGrowthForecast"
            type="number"
            value={formData.projections.revenueGrowthForecast}
            onChange={(e) => handleChange('revenueGrowthForecast', parseFloat(e.target.value) || 0)}
          />
          <p className="text-xs text-muted-foreground">{t.step8.revenueGrowthHint}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ebitdaGrowthForecast">{t.step8.ebitdaGrowthForecast} *</Label>
          <Input
            id="ebitdaGrowthForecast"
            type="number"
            value={formData.projections.ebitdaGrowthForecast}
            onChange={(e) => handleChange('ebitdaGrowthForecast', parseFloat(e.target.value) || 0)}
          />
          <p className="text-xs text-muted-foreground">{t.step8.ebitdaGrowthHint}</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="growthOpportunities">{t.step8.growthOpportunities}</Label>
        <Textarea
          id="growthOpportunities"
          value={formData.projections.growthOpportunities}
          onChange={(e) => handleChange('growthOpportunities', e.target.value)}
          placeholder={t.step8.growthOpportunitiesPlaceholder}
          rows={4}
        />
      </div>

      <div className="border-t pt-6">
        <h4 className="text-lg font-medium mb-4">{t.step8.sellerFinancing}</h4>
        
        <div className="space-y-4">
          <Label>{t.step8.sellerFinancingOffered}</Label>
          <RadioGroup
            value={formData.projections.sellerFinancingOffered ? 'yes' : 'no'}
            onValueChange={(value) => handleChange('sellerFinancingOffered', value === 'yes')}
            className="flex space-x-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="financingYes" />
              <Label htmlFor="financingYes" className="font-normal">{t.step3.yes}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="financingNo" />
              <Label htmlFor="financingNo" className="font-normal">{t.step3.no}</Label>
            </div>
          </RadioGroup>
        </div>

        {formData.projections.sellerFinancingOffered && (
          <div className="space-y-6 mt-6 p-4 bg-muted/30 rounded-lg border">
            {/* Row 1: Amount & Percentage */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sfAmount">{t.step8.sfAmount}</Label>
                <CurrencyInput
                  id="sfAmount"
                  value={formData.projections.sellerFinancingAmount || ''}
                  onChange={(v) => handleChange('sellerFinancingAmount', v === '' ? 0 : parseFloat(v))}
                />
                <p className="text-xs text-muted-foreground">{t.step8.sfAmountHint}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sfPercentage">{t.step8.sfPercentage}</Label>
                <Input
                  id="sfPercentage"
                  type="number"
                  min={0}
                  max={100}
                  value={formData.projections.sellerFinancingPercentage || ''}
                  onChange={(e) => handleChange('sellerFinancingPercentage', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">{t.step8.sfPercentageHint}</p>
              </div>
            </div>

            {/* Row 2: Interest Rate & Term */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sfRate">{t.step8.sfInterestRate}</Label>
                <Input
                  id="sfRate"
                  type="number"
                  step="0.1"
                  value={formData.projections.sellerFinancingInterestRate || ''}
                  onChange={(e) => handleChange('sellerFinancingInterestRate', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">{t.step8.sfInterestRateHint}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sfTerm">{t.step8.sfTermMonths}</Label>
                <Input
                  id="sfTerm"
                  type="number"
                  value={formData.projections.sellerFinancingTermMonths || ''}
                  onChange={(e) => handleChange('sellerFinancingTermMonths', parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">{t.step8.sfTermHint}</p>
              </div>
            </div>

            {/* Row 3: Down Payment & Standby */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sfDown">{t.step8.sfDownPayment}</Label>
                <Input
                  id="sfDown"
                  type="number"
                  min={0}
                  max={100}
                  value={formData.projections.sellerFinancingDownPaymentPercent || ''}
                  onChange={(e) => handleChange('sellerFinancingDownPaymentPercent', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">{t.step8.sfDownPaymentHint}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sfStandby">{t.step8.sfStandby}</Label>
                <Input
                  id="sfStandby"
                  type="number"
                  value={formData.projections.sellerFinancingStandbyPeriodMonths || ''}
                  onChange={(e) => handleChange('sellerFinancingStandbyPeriodMonths', parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">{t.step8.sfStandbyHint}</p>
              </div>
            </div>

            {/* Collateral */}
            <div className="space-y-2">
              <Label>{t.step8.sfCollateral}</Label>
              <Select
                value={formData.projections.sellerFinancingCollateralType}
                onValueChange={(v) => handleChange('sellerFinancingCollateralType', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.step8.sfCollateralHint} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(sfCollateralOpts).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Additional notes */}
            <div className="space-y-2">
              <Label htmlFor="sfNotes">{t.step8.sfNotes}</Label>
              <Textarea
                id="sfNotes"
                value={formData.projections.sellerFinancingNotes}
                onChange={(e) => handleChange('sellerFinancingNotes', e.target.value)}
                placeholder={t.step8.sfNotesPlaceholder}
                rows={3}
              />
            </div>

            {/* Legacy freeform details */}
            <div className="space-y-2">
              <Label htmlFor="sellerFinancingDetails">{t.step8.sellerFinancingDetails}</Label>
              <Textarea
                id="sellerFinancingDetails"
                value={formData.projections.sellerFinancingDetails}
                onChange={(e) => handleChange('sellerFinancingDetails', e.target.value)}
                placeholder={t.step8.sellerFinancingPlaceholder}
                rows={3}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};